// Support tickets — platform-wide triage. Each ticket carries the hidden
// diagnostic context the org submitter never saw: current page, a breadcrumb
// trail of their recent navigation/API activity, browser, and build — enough
// to reproduce the issue without a back-and-forth.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api.js';
import ActionMenu from '../components/ActionMenu.jsx';
import { formatContextValue, isStructured } from '../lib/contextValue.js';

const STATUS_STYLE = {
  open:        'bg-amber-100 text-amber-800',
  in_progress: 'bg-blue-100 text-blue-700',
  closed:      'bg-gray-100 text-gray-500',
};
const STATUS_LABEL = { open: 'Open', in_progress: 'In progress', closed: 'Closed' };
const FILTERS = ['all', 'open', 'in_progress', 'closed'];

function fmtWhen(ts) {
  if (!ts) return '—';
  return new Date(`${String(ts).replace(' ', 'T')}Z`).toLocaleString([], {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// 4xx AND WORSE IS NOT THE SAME AS WRONG — a 487 is the required answer to a CANCEL, a 486 is a
// busy phone. Mirrors the org dashboard's table; these two packages cannot share imports across the
// pnpm workspace boundary, so the list is duplicated deliberately.
const NORMAL_SIP_OUTCOMES = new Set(['401', '407', '487', '486', '603']);

const clockOf = (ts) => {
  const n = Number(ts);
  if (!Number.isFinite(n) || n <= 0) return String(ts ?? '');
  return new Date(n * 1000).toLocaleTimeString([], { hour12: false });
};

/**
 * A call trace submitted with a ticket.
 *
 * Filed against itself, 2026-08-22: the org side attached this as a nested object and the panel
 * below rendered every field with `String(v)`, so the whole trace read `[object Object]` to the
 * only person meant to act on it. The attachment was fine; the reader was not.
 */
function TraceAttachment({ attachment, omitted = 0 }) {
  const rows = attachment.rows || [];
  const ours = new Set((attachment.extensions || []).map(String));
  return (
    <div>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
        {attachment.label || attachment.kind || 'Attachment'}
      </div>
      <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-xs">
        <p className="text-gray-600">
          {attachment.host && <>Host {attachment.host}. </>}
          {typeof attachment.matched === 'number' && (
            <>{rows.length} of {attachment.matched} matched messages
              {typeof attachment.total_in_ring === 'number' && <> ({attachment.total_in_ring} in the host buffer)</>}.
            </>
          )}
          {omitted > 0 && <> {omitted} older message{omitted === 1 ? '' : 's'} trimmed to fit.</>}
        </p>
        {attachment.other_extensions?.length > 0 && (
          // The org side matches by address when a trace is scoped to an IP, so every device behind
          // that router is included. Triage has to know that before reading the table.
          <p className="mt-0.5 text-amber-800">
            Includes traffic from other extensions behind the same address:{' '}
            {attachment.other_extensions.join(', ')}.
          </p>
        )}
        {(attachment.findings || []).map((f) => (
          <p key={f.text} className={`mt-0.5 ${f.level === 'warn' ? 'text-amber-800' : 'text-gray-600'}`}>
            {f.text}
          </p>
        ))}
        {rows.length > 0 && (
          <div className="mt-2 max-h-80 overflow-auto">
            <table className="w-full border-collapse text-left font-mono text-[11px]">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-0.5 pr-2 font-medium">#</th>
                  <th className="py-0.5 pr-2 font-medium">Time</th>
                  <th className="py-0.5 pr-2 font-medium">Dir</th>
                  <th className="py-0.5 pr-2 font-medium">Address</th>
                  <th className="py-0.5 font-medium">Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const u = /sip:(\d{3,})@/.exec(String(r.message))?.[1];
                  const foreign = u && ours.size > 0 && !ours.has(u);
                  const bad = r.status && Number(r.status) >= 400 && !NORMAL_SIP_OUTCOMES.has(String(r.status));
                  return (
                    <tr key={r.seq} className={`border-b border-gray-100 align-top ${foreign ? 'bg-amber-50' : ''}`}>
                      <td className="py-0.5 pr-2 text-gray-400">{r.seq}</td>
                      <td className="py-0.5 pr-2 whitespace-nowrap text-gray-500">{clockOf(r.ts)}</td>
                      <td className={`py-0.5 pr-2 ${r.direction === 'in' ? 'text-blue-700' : 'text-gray-500'}`}>{r.direction}</td>
                      <td className="py-0.5 pr-2 text-gray-600">{r.address}</td>
                      <td className={`py-0.5 break-all ${bad ? 'text-red-700' : 'text-gray-800'}`}>
                        {r.message}
                        {foreign && <span className="ml-1 rounded bg-amber-100 px-1 text-amber-800">ext {u}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// The hidden diagnostic payload, rendered for triage: context fields up top,
// breadcrumb trail newest-last (exactly the order the user did things).
function ContextPanel({ context }) {
  if (!context) return <p className="text-sm text-gray-400">No diagnostic context attached.</p>;
  const {
    breadcrumbs = [], attachment = null, attachment_rows_omitted: omitted = 0, ...fields
  } = context;
  // The exact page the user was on when they filed the ticket. Rendered as a
  // deep-link so triage lands on the same org-dashboard screen in one click
  // (opens the org subdomain in a new tab).
  const pageUrl = context.page || context.url || null;
  const isHttp = typeof pageUrl === 'string' && /^https?:\/\//.test(pageUrl);
  return (
    <div className="space-y-3">
      {isHttp && (
        <a
          href={pageUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-md border border-blue-300 bg-blue-50 px-3 py-1.5 text-xs font-medium text-blue-700 hover:bg-blue-100"
          title={pageUrl}
        >
          Open reported page ↗
        </a>
      )}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
        {Object.entries(fields).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <dt className="w-24 flex-shrink-0 font-medium uppercase tracking-wide text-gray-400">{k}</dt>
            <dd className="break-all font-mono text-gray-700">
              {(k === 'page' || k === 'url') && /^https?:\/\//.test(String(v))
                ? <a href={String(v)} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{String(v)}</a>
                // Anything structured gets printed, not coerced. `String({})` is "[object Object]",
                // which is how a submitted call trace became unreadable in triage.
                : isStructured(v)
                  ? <pre className="whitespace-pre-wrap break-all">{formatContextValue(v)}</pre>
                  : formatContextValue(v)}
            </dd>
          </div>
        ))}
      </dl>
      {attachment && typeof attachment === 'object' && (
        <TraceAttachment attachment={attachment} omitted={Number(omitted) || 0} />
      )}
      {breadcrumbs.length > 0 && (
        <div>
          <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">
            Activity trail ({breadcrumbs.length})
          </div>
          <div className="max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-gray-50 font-mono text-xs">
            {breadcrumbs.map((b, i) => (
              <div key={i} className={`flex gap-3 px-3 py-1 ${b.kind === 'api_error' ? 'bg-red-50 text-red-700' : 'text-gray-600'}`}>
                <span className="whitespace-nowrap text-gray-400">{String(b.t || '').slice(11, 19)}</span>
                <span className="w-16 flex-shrink-0">{b.kind}</span>
                <span className="break-all">
                  {b.kind === 'nav' ? b.path : `${b.method} ${b.path} → ${b.status}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// One entry in a ticket's history. Author + time on every line, because the
// whole reason this replaced a single notes field is knowing who said what when.
function Comment({ c }) {
  const isSystem = c.author_kind === 'system';
  return (
    <li className="flex gap-2.5 py-2.5">
      <span className={`mt-0.5 inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
        isSystem ? 'bg-gray-200 text-gray-600' : 'bg-blue-100 text-blue-700'
      }`}>
        {isSystem ? '·' : String(c.author_name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase()}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-medium text-gray-900">{c.author_name || 'Unknown'}</span>
          {c.author_kind === 'org' && (
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600">customer</span>
          )}
          <span className="text-xs text-gray-400">{fmtWhen(c.created_at)}</span>
        </div>
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-gray-800">{c.body}</p>
      </div>
    </li>
  );
}

function TicketRow({ ticket, onStatus, statusPending }) {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [closeOnPost, setCloseOnPost] = useState(false);
  const [postErr, setPostErr] = useState(null);
  const queryClient = useQueryClient();
  const comments = Array.isArray(ticket.comments) ? ticket.comments : [];

  // Comments are append-only, so posting is the only write — there's no edit
  // path to build and no "unsaved changes" state to lose.
  const addComment = useMutation({
    mutationFn: () => api.post(`/verso-admin/support-tickets/${ticket.id}/comments`, {
      body: draft,
      ...(closeOnPost ? { status: 'closed' } : {}),
    }),
    onSuccess: () => {
      setDraft(''); setCloseOnPost(false); setPostErr(null);
      queryClient.invalidateQueries({ queryKey: ['support-tickets-admin'] });
    },
    onError: (e) => setPostErr(e?.body?.error || e?.message || 'Could not post that comment.'),
  });

  return (
    <>
      <tr className={ticket.status === 'open' ? 'bg-amber-50/40' : ''}>
        <td className="px-4 py-2">
          <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[ticket.status]}`}>
            {STATUS_LABEL[ticket.status] || ticket.status}
          </span>
        </td>
        <td className="px-4 py-2">
          <button onClick={() => setExpanded((v) => !v)} className="text-left font-medium text-blue-600 hover:underline">
            #{ticket.id} · {ticket.subject}
          </button>
        </td>
        <td className="px-4 py-2 text-gray-600">
          <Link to={`/orgs/${ticket.org_id}`} className="text-blue-600 hover:underline">{ticket.org_name || ticket.org_id}</Link>
        </td>
        <td className="px-4 py-2 text-gray-600">{ticket.submitted_by || '—'}</td>
        <td className="px-4 py-2 text-gray-500">{fmtWhen(ticket.created_at)}</td>
        <td className="px-4 py-2 text-right">
          <ActionMenu
            title={`Actions for ticket #${ticket.id}`}
            items={[
              { label: expanded ? 'Hide details' : 'View details', onClick: () => setExpanded((v) => !v) },
              { divider: true },
              ...(ticket.status !== 'in_progress'
                ? [{ label: 'Mark in progress', onClick: () => onStatus(ticket.id, 'in_progress'), disabled: statusPending }] : []),
              ...(ticket.status !== 'closed'
                ? [{ label: 'Close ticket', onClick: () => onStatus(ticket.id, 'closed'), disabled: statusPending }] : []),
              ...(ticket.status === 'closed'
                ? [{ label: 'Reopen', onClick: () => onStatus(ticket.id, 'open'), disabled: statusPending }] : []),
            ]}
          />
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-gray-50/60 px-6 py-4">
            <div className="grid gap-6 lg:grid-cols-2">
              <div className="space-y-3">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Reported by {ticket.submitted_by || 'unknown'} ({ticket.submitter_email || '—'})</div>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">
                    {ticket.description || <span className="italic text-gray-400">No description provided.</span>}
                  </p>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    History{comments.length ? ` · ${comments.length}` : ''}
                  </div>
                  {comments.length === 0 ? (
                    <p className="mt-1 text-sm italic text-gray-400">
                      No comments yet — add the first one below.
                    </p>
                  ) : (
                    <ul className="mt-1 divide-y divide-gray-200 border-y border-gray-200">
                      {comments.map((c) => <Comment key={c.id} c={c} />)}
                    </ul>
                  )}

                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    placeholder="What you found, what you tried, what fixed it…"
                    className="mt-3 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                  />
                  {postErr && <p className="mt-1 text-xs text-red-600">{postErr}</p>}
                  <div className="mt-1 flex flex-wrap items-center gap-3">
                    <button onClick={() => addComment.mutate()}
                      disabled={addComment.isPending || !draft.trim()}
                      className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                      {addComment.isPending ? 'Posting…' : 'Add comment'}
                    </button>
                    {ticket.status !== 'closed' && (
                      <label className="flex items-center gap-1.5 text-xs text-gray-600">
                        <input type="checkbox" checked={closeOnPost}
                               onChange={(e) => setCloseOnPost(e.target.checked)}
                               className="rounded border-gray-300" />
                        Close the ticket with this comment
                      </label>
                    )}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    Comments can&rsquo;t be edited or deleted — they&rsquo;re the record of how the ticket was worked.
                  </p>
                </div>
              </div>
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-gray-500">Diagnostic context (hidden from submitter)</div>
                <ContextPanel context={ticket.context} />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export default function SupportTickets() {
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState('all');
  const { data, isLoading } = useQuery({
    queryKey: ['support-tickets-admin', filter],
    queryFn: () => api.get('/verso-admin/support-tickets', filter === 'all' ? undefined : { status: filter }),
    refetchInterval: 60_000,
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => api.put(`/verso-admin/support-tickets/${id}`, { status }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['support-tickets-admin'] }),
  });

  const tickets = data?.tickets || [];
  const openCount = tickets.filter((t) => t.status === 'open').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Support</h1>
          <p className="mt-1 text-sm text-gray-500">
            Tickets from org dashboards, each with the submitter&rsquo;s page + activity trail attached.
          </p>
        </div>
        <div className="flex gap-1">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium ${
                filter === f ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}>
              {f === 'all' ? 'All' : STATUS_LABEL[f]}
              {f === 'open' && openCount > 0 && filter !== 'open' && ` (${openCount})`}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Ticket</th>
              <th className="px-4 py-2">Organization</th>
              <th className="px-4 py-2">Submitted by</th>
              <th className="px-4 py-2">Created</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">Loading…</td></tr>
            ) : tickets.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No tickets{filter !== 'all' ? ` with status "${STATUS_LABEL[filter]}"` : ' yet'}.</td></tr>
            ) : tickets.map((t) => (
              <TicketRow key={t.id} ticket={t}
                onStatus={(id, status) => statusMutation.mutate({ id, status })}
                statusPending={statusMutation.isPending} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
